import React, { useState, useEffect, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { Popover, Tabs, Tab, Box } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { HexColorPicker, HexColorInput } from "react-colorful";
import ColorPaletteGrid from "components/ColorPaletteGrid/ColorPaletteGrid";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import { Colors } from "utils/Constants";

export const AdvancedColorPickerContent = memo(
  ({ colors, selectedColor, onSelect, onGridSelect, size = 32 }) => {
    const [tabValue, setTabValue] = useState(0);
    const [localColor, setLocalColor] = useState(selectedColor || "#ffffff");
    const isCustomColor = useMemo(
      () => Boolean(selectedColor && !colors.includes(selectedColor)),
      [selectedColor, colors]
    );

    useEffect(() => {
      setLocalColor(selectedColor || "#ffffff");
    }, [selectedColor]);

    useEffect(() => {
      setTabValue(isCustomColor ? 1 : 0);
    }, [isCustomColor]);

    const handleTabChange = (event, newValue) => {
      setTabValue(newValue);
    };

    const handleCustomColorChange = (color) => {
      setLocalColor(color);
    };

    return (
      <Box sx={{ width: 280 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: "40px",
              "& .MuiTabs-indicator": {
                backgroundColor: Colors.PRIMARY,
                height: "3px",
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab
              label="Standard"
              sx={{
                textTransform: "none",
                minWidth: "auto",
                minHeight: "40px",
                fontSize: "14px",
                py: 1,
                fontWeight: tabValue === 0 ? 600 : 400,
                color: tabValue === 0 ? Colors.PRIMARY : "#667085",
                "&.Mui-selected": {
                  color: Colors.PRIMARY,
                },
              }}
            />
            <Tab
              label="Custom"
              sx={{
                textTransform: "none",
                minWidth: "auto",
                minHeight: "40px",
                fontSize: "14px",
                py: 1,
                fontWeight: tabValue === 1 ? 600 : 400,
                color: tabValue === 1 ? Colors.PRIMARY : "#667085",
                "&.Mui-selected": {
                  color: Colors.PRIMARY,
                },
              }}
            />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <ColorPaletteGrid
              colors={colors}
              selectedColor={selectedColor}
              onSelect={(color) => {
                if (onGridSelect) {
                  onGridSelect(color);
                } else {
                  onSelect(color);
                }
              }}
              size={Math.max(24, size)}
              gap={1}
              columns={5}
            />
          </Box>
        )}

        {tabValue === 1 && (
          <Box
            sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
          >
            <HexColorPicker
              color={localColor}
              onChange={handleCustomColorChange}
              style={{ width: "100%", height: "150px" }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <MDBox
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "4px",
                  backgroundColor: localColor || "#ffffff",
                  border: "1px solid rgba(0,0,0,0.1)",
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
                  flexShrink: 0,
                }}
              />
              <MDTypography variant="caption" fontWeight="medium">
                Hex:
              </MDTypography>
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  px: 1,
                  py: 0.5,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <MDTypography variant="caption" sx={{ color: "#666", mr: 0.5 }}>
                  #
                </MDTypography>
                <HexColorInput
                  color={localColor}
                  onChange={handleCustomColorChange}
                  prefixed={false}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    color: "#344054",
                    background: "transparent",
                  }}
                />
              </Box>
            </Box>
            <BasicButton
              title="Apply"
              background={Colors.PRIMARY}
              color={Colors.WHITE}
              action={() => {
                if (onGridSelect) {
                  onGridSelect(localColor);
                } else {
                  onSelect(localColor);
                }
              }}
              style={{ width: "100%", marginTop: "8px", btnMarginLeft: 0 }}
            />
          </Box>
        )}
      </Box>
    );
  }
);

AdvancedColorPickerContent.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedColor: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onGridSelect: PropTypes.func,
  size: PropTypes.number,
};

AdvancedColorPickerContent.defaultProps = {
  selectedColor: "",
  onGridSelect: null,
  size: 32,
};

const AdvancedColorPickerButton = memo(
  ({ colors, selectedColor, onSelect, size = 32, triggerStyle = {}, disabled = false }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
      if (!disabled) {
        setAnchorEl(event.currentTarget);
      }
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "advanced-color-popover" : undefined;

    return (
      <>
        <MDBox
          onClick={handleClick}
          sx={{
            width: size,
            height: size,
            backgroundColor: selectedColor || "#e0e0e0",
            borderRadius: "4px",
            border: selectedColor ? "2px solid #000" : "2px solid #bdbdbd",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            "&:hover": {
              boxShadow: disabled ? "none" : "0 2px 8px rgba(0,0,0,0.2)",
            },
            ...triggerStyle,
          }}
          aria-describedby={id}
        />
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              backgroundColor: "#fff",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
              borderRadius: "8px",
              overflow: "hidden",
              zIndex: 1500,
            },
          }}
        >
          <AdvancedColorPickerContent
            colors={colors}
            selectedColor={selectedColor}
            onSelect={onSelect}
            onGridSelect={(color) => {
              onSelect(color);
              handleClose();
            }}
            size={24}
          />
        </Popover>
      </>
    );
  }
);

export default AdvancedColorPickerButton;

AdvancedColorPickerButton.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedColor: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  size: PropTypes.number,
  triggerStyle: PropTypes.shape({}),
  disabled: PropTypes.bool,
};

AdvancedColorPickerButton.defaultProps = {
  selectedColor: "",
  size: 32,
  triggerStyle: {},
  disabled: false,
};
